/**
 * PropertiesController — /api/v1/properties
 * Mirrors: src/Aidepo.Api/Controllers/PropertiesController.cs
 */
import { apiDelete, apiGet, apiPatch, apiPost } from '../client.js';
import type { IsoDateTime, Uuid } from '../types.js';

export interface PropertySummary {
  readonly id: Uuid;
  readonly name: string;
  readonly type: string;
}

export interface ItemNode {
  readonly id: Uuid;
  readonly name: string;
  readonly quantity: number;
  /**
   * Manually picked icon name (from tokens ICON_NAMES), or null when the
   * front-end should auto-resolve it from the item name (hh-345). May arrive
   * as `undefined` from older payloads — treat both as "auto".
   */
  readonly icon?: string | null;
  /**
   * True when the item has no room, property, and container (case-06).
   * Returned by GET /api/v1/properties/tree as of the Phase 2 backend.
   */
  readonly isOrphan: boolean;
  /**
   * Id of the container item this item is nested inside, or null.
   * NB: the /tree endpoint does NOT currently project this field — it is
   * declared here for forward-compat with the Item entity (Data/Entities/Item.cs)
   * and may arrive as `undefined` from the tree response. Treat both
   * `null` and `undefined` as "not in a container".
   */
  readonly containerId?: Uuid | null;
  /**
   * Free-text placement detail below room level (case-40), e.g. "верхний ящик
   * стола". null/undefined when not specified. Surfaced on the item card under
   * the location label.
   */
  readonly locationNote?: string | null;
  /**
   * Item creation timestamp (ISO 8601). Projected by GET /api/v1/properties/tree
   * for the «Недавно добавленные» sort on the flat items list (aidepo-63.6). May
   * arrive as `undefined` from older payloads — treat as "unknown / oldest".
   */
  readonly createdAt?: IsoDateTime;
}

export interface EquipmentNode {
  readonly id: Uuid;
  readonly name: string;
  readonly brand: string | null;
  readonly model: string | null;
  readonly category: string;
  /** Manually picked icon name, or null/undefined for auto-resolve (hh-345). */
  readonly icon?: string | null;
}

export interface RoomNode {
  readonly id: Uuid;
  readonly name: string;
  /** Manually picked icon name, or null/undefined for auto-resolve (hh-345). */
  readonly icon?: string | null;
  readonly items: readonly ItemNode[];
  readonly equipment: readonly EquipmentNode[];
}

export interface PropertyTreeNode {
  readonly id: Uuid;
  readonly name: string;
  readonly type: string;
  readonly rooms: readonly RoomNode[];
  readonly items: readonly ItemNode[];
  readonly equipment: readonly EquipmentNode[];
}

export interface PropertyTreeResponse {
  readonly properties: readonly PropertyTreeNode[];
}

export interface CreatePropertyRequest {
  readonly name: string;
  readonly type?: string;
  readonly description?: string;
}

export interface UpdatePropertyRequest {
  readonly name?: string;
  readonly type?: string;
  readonly description?: string;
}

/** GET /api/v1/properties/tree — full tree of properties/rooms/items/equipment. */
export function getPropertyTree(): Promise<PropertyTreeResponse> {
  return apiGet<PropertyTreeResponse>('/api/v1/properties/tree');
}

/** GET /api/v1/properties — flat list, short shape. */
export function listProperties(): Promise<readonly PropertySummary[]> {
  return apiGet<readonly PropertySummary[]>('/api/v1/properties');
}

/** POST /api/v1/properties — create a property. */
export function createProperty(req: CreatePropertyRequest): Promise<PropertySummary> {
  return apiPost<PropertySummary, CreatePropertyRequest>('/api/v1/properties', req);
}

/** PATCH /api/v1/properties/{id} — rename / re-type. */
export function updateProperty(id: Uuid, req: UpdatePropertyRequest): Promise<PropertySummary> {
  return apiPatch<PropertySummary, UpdatePropertyRequest>(`/api/v1/properties/${id}`, req);
}

/** DELETE /api/v1/properties/{id} — cascade-deletes rooms/items/equipment. */
export function deleteProperty(id: Uuid): Promise<void> {
  return apiDelete(`/api/v1/properties/${id}`);
}

export interface MovePropertyContentsRequest {
  readonly targetPropertyId: Uuid;
}

export interface MovePropertyContentsResponse {
  readonly moved: {
    readonly rooms: number;
    readonly items: number;
    readonly equipment: number;
  };
  readonly changeRecordId: Uuid;
}

/**
 * POST /api/v1/properties/{id}/move-contents — альтернатива каскадному
 * удалению (CASE-35): переносит помещения и привязанные напрямую вещи/
 * оборудование в другую собственность и удаляет опустевшую исходную.
 * Обратимо через общий undo (ChangeRecord kind="reparent").
 */
export function moveContents(
  id: Uuid,
  req: MovePropertyContentsRequest,
): Promise<MovePropertyContentsResponse> {
  return apiPost<MovePropertyContentsResponse, MovePropertyContentsRequest>(
    `/api/v1/properties/${id}/move-contents`,
    req,
  );
}
